export enum ParentGender {
  MALE = "male",
  FEMALE = "female",
  OTHER = "other",
  PREFER_NOT_TO_SAY = "prefer_not_to_say",
  NOT_SPECIFIED = "not_specified",
}

export enum AnnualIncome {
  UNDER_5M = "under_5m",
  M5_10M = "5m_10m",
  OVER_10M = "over_10m",
  PREFER_NOT_TO_SAY = "prefer_not_to_say",
}

export enum HouseholdMember {
  MOTHER = "mother",
  FATHER = "father",
  GRANDMOTHER = "grandmother",
  GRANDFATHER = "grandfather",
}

export enum UserRole {
  MOTHER = "mother",
  FATHER = "father",
  GRANDMOTHER = "grandmother",
  GRANDFATHER = "grandfather",
  OTHER = "other",
}

export enum Glasses {
  NONE = "none",
  GLASSES = "glasses",
}

export enum Hairstyle {
  VERY_SHORT = "very_short",
  SHORT = "short",
  MEDIUM = "medium",
  SLIGHTLY_LONG = "slightly_long",
  LONG = "long",
}

export enum BodyType {
  THIN = "thin",
  SLIGHTLY_THIN = "slightly_thin",
  MEDIUM = "medium",
  SLIGHTLY_CHUBBY = "slightly_chubby",
  CHUBBY = "chubby",
}

export enum FaceShape {
  ROUND = "round",
  OVAL = "oval",
  OBLONG = "oblong",
  SQUARE = "square",
  TRIANGLE = "triangle",
}

export type AccountProfile = {
  id?: number;
  account_uuid?: string;
  birthday: string | null;
  gender: ParentGender | null;
  locale: string;
  // お勤め先
  industry: string | null;
  occupation: string | null;
  job_title: string | null;
  organization: string | null;
  annual_income: AnnualIncome | null;
  // 住所
  region: string | null;
  city: string | null;
  // 自分の役割（mother/father/grandmother/grandfather または自由入力テキスト）
  user_role: string | null;
  // 家族
  household_members: string[] | null;
  boys_count: number | null;
  girls_count: number | null;
  // 容姿
  glasses: Glasses | null;
  hairstyle: Hairstyle | null;
  body_type: BodyType | null;
  face_shape: FaceShape | null;
  self_introduction: string | null;
};

export type ParentState = {
  parentProfile: AccountProfile | null;
};

// RouteProps and Page Types
export interface RouteProps {
  location: string;
  navigate: (path: string) => void;
  close: () => void;
}

export enum ParentPageTab {
  ACCOUNT = "account",
  PLAYER = "player",
  MESSAGE = "message",
  SUBSCRIPTION = "subscription",
  NOTIFICATION = "notification",
  OTHER = "other",
}

// Feedback Types
export enum FeedbackHistoryType {
  RATING = "rating",
  FEEDBACK = "feedback",
  CONTACT = "contact",
}

export enum ContactType {
  HOW_TO = "how_to",
  BUG = "bug",
  REQUEST = "request",
  OTHER = "other",
}

export interface FeedbackHistoryItem {
  id: string;
  type: FeedbackHistoryType;
  created_at: string;
  rating?: number;
  complaint_type?: string;
  detail?: string;
  contact_type?: ContactType;
  reply?: {
    title: string;
    content: string;
    published_at: string;
  } | null;
}

// User Activity Types
export const UserActivityType = {
  COURSE_LEVEL_CLEAR: "course_level_clear",
  PROJECT_CREATED: "project_created",
  PROJECT_PUBLISHED: "project_published",
  PROJECT_FAVORITED: "project_favorited",
  PROJECT_FAVORITED_RECEIVED: "project_favorited_received",
  LOGIN: "login",
  ICON_CHANGED: "icon_changed",
  AI_CHAT_SESSION_STARTED: "ai_chat_session_started",
  INQUIRY_SENT: "inquiry_sent",
  APP_REVIEWED: "app_reviewed",
  FEEDBACK_SENT: "feedback_sent",
  COIN_EARNED: "coin_earned",
  COIN_SPENT: "coin_spent",
  LOGIN_STREAK_MILESTONE: "login_streak_milestone",
} as const;

export type UserActivityTypeValue = (typeof UserActivityType)[keyof typeof UserActivityType];

export interface CourseClearActivityData {
  course_id: number;
  course_type: string;
  stage_id: number;
  level_id: number;
  is_first_clear: boolean;
  used_tips: boolean;
}

export interface ProjectActivityData {
  project_id: string;
  project_name: string;
  project_type?: string;
  author_uid?: string;
  favorited_by_uid?: string;
}

export interface LoginActivityData {
  login_env: string;
  login_count: number;
}

export interface IconChangedActivityData {
  old_icon: string | null;
  new_icon: string;
}

export interface CoinActivityData {
  amount: number;
  source: string;
  new_balance: number;
}

export interface LoginStreakMilestoneActivityData {
  milestone_days: number;
  bonus_coins: number;
  current_streak: number;
}

export type ActivityData =
  | CourseClearActivityData
  | ProjectActivityData
  | LoginActivityData
  | IconChangedActivityData
  | CoinActivityData
  | LoginStreakMilestoneActivityData
  | Record<string, unknown>;

export interface UserActivityLog {
  id: number;
  uid: string;
  activity_type: UserActivityTypeValue;
  activity_data: ActivityData | null;
  activity_at: string;
  created_at: string;
}

export interface GetActivitiesResponse {
  activities: UserActivityLog[];
  total: number;
  has_more: boolean;
}

// AI Chat Types
export interface AIChatSession {
  id: string;
  title: string;
  message_count: number;
  last_message: {
    content: string;
    role: "user" | "assistant";
    created_at: string;
  } | null;
  created_at: string;
}

export interface GetAIChatSessionsResponse {
  sessions: AIChatSession[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AIChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  image_urls: string[] | null;
  created_at: string;
}

export interface GetAIChatMessagesResponse {
  session: {
    id: string;
    child_uid: string;
    title: string;
    created_at: string;
  };
  messages: AIChatMessage[];
  has_more: boolean;
}

/** 子どもが書いたピースのコメント（保護者ページの一覧用） */
export type ParentPieceComment = {
  id: string;
  piece_id: string;
  piece_name: string | null;
  parent_comment_id: string | null;
  content: string;
  status: string;
  created_at: string;
};

/** 子どもが出した通報（保護者ページの履歴用） */
/** 通報した相手の種類 */
export enum ParentReportTarget {
  PIECE = "piece",
  COMMENT = "comment",
  PROJECT = "project",
}

/** piece_* はピース・コメントの通報、project_* はアプリ（じゆうにつくる）の通報のときだけ値が入る */
export type ParentPieceReport = {
  id: string;
  target: ParentReportTarget;
  reason: string;
  detail: string | null;
  status: string;
  created_at: string;
  piece_id: string | null;
  piece_name: string | null;
  comment_id: string | null;
  comment_content: string | null;
  project_id: string | null;
  project_name: string | null;
};
